import { DefaultLinesDiffComputer, type LinesDiff } from "vscode-diff"

export interface DiffLabLineRange {
    startLineNumber: number
    endLineNumberExclusive: number
}

export interface DiffLabChange {
    original: DiffLabLineRange
    modified: DiffLabLineRange
}

export interface DiffLabMove extends DiffLabChange {
    nestedChanges: DiffLabChange[]
}

export interface DiffLabAnalysis {
    moves: DiffLabMove[]
    residualChanges: DiffLabChange[]
    allChanges: DiffLabChange[]
    hitTimeout: boolean
    elapsedMs: number
    moveDetectionError?: string
}

export interface DiffLabSlice {
    contents: string
    startLine: number
    endLine: number
}

function lines(contents: string): string[] {
    return contents.split(/\r?\n/)
}

function copyRange(range: DiffLabLineRange): DiffLabLineRange {
    return {
        startLineNumber: range.startLineNumber,
        endLineNumberExclusive: range.endLineNumberExclusive,
    }
}

function rangeIsWithin(candidate: DiffLabLineRange, container: DiffLabLineRange): boolean {
    return candidate.startLineNumber >= container.startLineNumber && candidate.endLineNumberExclusive <= container.endLineNumberExclusive
}

function changeBelongsToMove(change: DiffLabChange, move: DiffLabMove): boolean {
    return rangeIsWithin(change.original, move.original) || rangeIsWithin(change.modified, move.modified)
}

export function analyzeDiff(before: string, after: string, ignoreTrimWhitespace: boolean): DiffLabAnalysis {
    const started = performance.now()
    const computer = new DefaultLinesDiffComputer()
    const originalLines = lines(before)
    const modifiedLines = lines(after)
    let result: LinesDiff
    let moveDetectionError: string | undefined
    try {
        result = computer.computeDiff(originalLines, modifiedLines, {
            ignoreTrimWhitespace,
            computeMoves: true,
            maxComputationTimeMs: 250,
        })
    } catch (cause) {
        moveDetectionError = cause instanceof Error ? cause.message : "Move detection failed"
        result = computer.computeDiff(originalLines, modifiedLines, {
            ignoreTrimWhitespace,
            computeMoves: false,
            maxComputationTimeMs: 250,
        })
    }
    const moves: DiffLabMove[] = result.moves.map((move) => ({
        original: copyRange(move.lineRangeMapping.original),
        modified: copyRange(move.lineRangeMapping.modified),
        nestedChanges: move.changes.map((change) => ({
            original: copyRange(change.original),
            modified: copyRange(change.modified),
        })),
    }))
    const allChanges: DiffLabChange[] = result.changes.map((change) => ({
        original: copyRange(change.original),
        modified: copyRange(change.modified),
    }))
    return {
        moves,
        allChanges,
        residualChanges: allChanges.filter((change) => !moves.some((move) => changeBelongsToMove(change, move))),
        hitTimeout: result.hitTimeout,
        elapsedMs: performance.now() - started,
        ...(moveDetectionError ? { moveDetectionError } : {}),
    }
}

export function sliceRange(contents: string, range: DiffLabLineRange, contextLines = 0): DiffLabSlice {
    const contentLines = lines(contents)
    const rangeStartIndex = Math.max(0, range.startLineNumber - 1)
    const rangeEndIndex = Math.max(rangeStartIndex, range.endLineNumberExclusive - 1)
    const startIndex = Math.max(0, rangeStartIndex - contextLines)
    const endIndex = Math.min(contentLines.length, rangeEndIndex + contextLines)
    return {
        contents: contentLines.slice(startIndex, endIndex).join("\n"),
        startLine: startIndex + 1,
        endLine: Math.max(startIndex + 1, endIndex),
    }
}

export function changedLineCount(changes: DiffLabChange[]): number {
    return changes.reduce((total, change) => {
        const oldLength = change.original.endLineNumberExclusive - change.original.startLineNumber
        const newLength = change.modified.endLineNumberExclusive - change.modified.startLineNumber
        return total + Math.max(oldLength, newLength)
    }, 0)
}
